import React, { useState } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  Link as MuiLink,
  InputAdornment,
} from '@mui/material';
import { Email } from '@mui/icons-material';
import { enqueueSnackbar } from 'notistack';
import { authService } from '../../services/auth.service';

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

export const ActivateAccountPage: React.FC = () => {
  const navigate = useNavigate();
  const query = useQuery();

  const [email, setEmail] = useState(query.get('email') || '');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);

  const handleActivate = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      await authService.activateAccount({ email, code });
      enqueueSnackbar('Account activated. You are now signed in.', { variant: 'success' });
      navigate('/app/dashboard');
    } catch (err: any) {
      const msg = err.response?.data?.error || err.response?.data?.detail || 'Activation failed';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    setError(null);
    try {
      const resp: any = await authService.resendActivationCode({ email });
      if (resp?.debug_activation_code) {
        enqueueSnackbar(`Activation code (dev): ${resp.debug_activation_code}`, { variant: 'info' });
      } else {
        enqueueSnackbar(resp.message || 'Activation code sent', { variant: 'success' });
      }
    } catch (err: any) {
      const msg = err.response?.data?.error || err.response?.data?.detail || 'Failed to resend code';
      setError(msg);
    } finally {
      setResending(false);
    }
  };

  return (
    <Container component="main" maxWidth="sm">
      <Box sx={{ marginTop: 8, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Paper elevation={3} sx={{ padding: 4, width: '100%' }}>
          <Typography component="h1" variant="h4" align="center" gutterBottom>
            Activate Account
          </Typography>
          <Typography variant="body1" align="center" color="text.secondary" sx={{ mb: 3 }}>
            Enter the activation code we emailed you.
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleActivate} noValidate>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              id="code"
              label="Activation Code"
              name="code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3 }}
              disabled={loading || !email || !code}
            >
              {loading ? 'Activating...' : 'Activate'}
            </Button>

            <Button
              type="button"
              fullWidth
              variant="outlined"
              sx={{ mt: 2 }}
              disabled={resending || !email}
              onClick={handleResend}
            >
              {resending ? 'Sending...' : 'Resend code'}
            </Button>

            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <MuiLink component={Link} to="/login" variant="body2">
                Back to login
              </MuiLink>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};


